import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type {
  ContentImageKind,
  IContentImage,
  IContentImageBody,
  IContentPlacement,
  IContentUpload
} from './media-contracts';

/**
 * survey-api's media library — `/api/v1/admin/website/media` — and the sole reader of it.
 *
 * It follows `posts-api.ts` in everything the surfaces share: the revision travels as an opaque
 * `ETag` quoted back in `If-Match`, the server filters, and nothing here judges a file.
 *
 * **`upload` sends the file as the body**, with its own type, exactly as the participant photo
 * does — not a multipart form. Nothing is checked before it goes: survey-api answers 400 for an
 * empty file, 413 for one over 5 MB and 415 for a type it does not take or a header it cannot
 * read a size from, and a check here would be a second judge of the server's rule and would
 * intercept the refusals the screen needs to show.
 *
 * **A crop is an upload with a provenance.** The browser cuts the picture — survey-api decodes
 * nothing — and sends the result with the image it came from and the rectangle it took. The
 * source is kept, which is WEB-09 AC3.
 */

const BASE = '/api/v1/admin/website/media';

function imagePath(imageId: string): string {
  return `${BASE}/${encodeURIComponent(imageId)}`;
}

function precondition(etag: string): { 'If-Match': string } {
  return { 'If-Match': etag };
}

/** The query an upload carries beside its bytes. */
function uploadQuery(upload: IContentUpload): string {
  const query = new URLSearchParams({ fileName: upload.fileName, kind: upload.kind });
  if (upload.alt) query.set('alt', upload.alt);
  if (upload.from) query.set('from', upload.from);
  if (upload.crop) {
    query.set('x', String(upload.crop.x));
    query.set('y', String(upload.crop.y));
    query.set('width', String(upload.crop.width));
    query.set('height', String(upload.crop.height));
  }
  return query.toString();
}

export const mediaApi = {
  /** The library, newest first. `kind` narrows it; `unused` leaves what nothing places. */
  list(
    options: { kind?: ContentImageKind; unused?: boolean } = {},
    signal?: AbortSignal
  ): Promise<IContentImage[]> {
    const query = new URLSearchParams();
    if (options.kind) query.set('kind', options.kind);
    if (options.unused) query.set('unused', 'true');
    const suffix = query.size > 0 ? `?${query}` : '';
    return apiRequest<IContentImage[]>(`${BASE}${suffix}`, { signal });
  },

  read(imageId: string, signal?: AbortSignal): Promise<IEtagged<IContentImage>> {
    return apiRequestWithEtag<IContentImage>(imagePath(imageId), { signal });
  },

  /** The file itself as the body (`client.ts` sends a `Blob` as itself). 201 with the new row. */
  upload(file: Blob, upload: IContentUpload): Promise<IEtagged<IContentImage>> {
    return apiRequestWithEtag<IContentImage>(`${BASE}?${uploadQuery(upload)}`, {
      method: 'POST',
      body: file
    });
  },

  /** The words about a file. Never its bytes — there is no route that rewrites one. */
  save(imageId: string, etag: string, body: IContentImageBody): Promise<IEtagged<IContentImage>> {
    return apiRequestWithEtag<IContentImage>(imagePath(imageId), {
      method: 'PUT',
      body,
      headers: precondition(etag)
    });
  },

  /**
   * WEB-09 AC2 — points the named placements at another image.
   *
   * Naming every placement is the requirement's first branch, "every placement updates"; naming
   * some is its second, "the editor is shown each placement and can choose". What is not moved
   * keeps this image, which stays in the library. Moving a placement is an edit to the record
   * holding it, so the post or person it belongs to gets a version saying what happened.
   */
  replace(
    imageId: string,
    etag: string,
    withImageId: string,
    placements: Pick<IContentPlacement, 'kind' | 'itemId'>[]
  ): Promise<IEtagged<IContentImage>> {
    return apiRequestWithEtag<IContentImage>(`${imagePath(imageId)}/replacement`, {
      method: 'POST',
      body: { withImageId, placements },
      headers: precondition(etag)
    });
  },

  /**
   * Removes an image nothing places and nothing was cut from — 409 `invalid-transition`
   * otherwise, naming where it is still used. The stored file stays: it is named by its content,
   * so another library row may be standing on the same bytes.
   */
  remove(imageId: string, etag: string): Promise<void> {
    return apiRequest<void>(imagePath(imageId), {
      method: 'DELETE',
      headers: precondition(etag)
    });
  }
};
